import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PostAdStep2.css';

type PriceType = 'fixed' | 'market' | 'floating';

const PostAdStep2: React.FC = () => {
  const navigate = useNavigate();
  
  const [unitPrice, setUnitPrice] = useState('0');
  const [totalAmount, setTotalAmount] = useState('0');
  const [priceType, setPriceType] = useState<PriceType>('fixed');
  const [minOrderLimit, setMinOrderLimit] = useState('0');
  const [maxOrderLimit, setMaxOrderLimit] = useState('0');
  
  const marketRate = 1439.0200;
  
  const handlePriceAdjustment = (adjustment: 'minus' | 'market' | 'plus') => {
    switch (adjustment) {
      case 'minus':
        setUnitPrice((marketRate * 0.99).toFixed(4));
        break;
      case 'market':
        setUnitPrice(marketRate.toFixed(4));
        break;
      case 'plus':
        setUnitPrice((marketRate * 1.01).toFixed(4));
        break;
    }
  };
  
  const handleNext = () => {
    if (parseFloat(unitPrice) <= 0) {
      alert('Please enter a valid unit price');
      return;
    }
    
    if (parseFloat(totalAmount) <= 0) {
      alert('Please enter a valid total amount');
      return;
    }
    
    if (parseFloat(minOrderLimit) <= 0 || parseFloat(maxOrderLimit) <= 0) {
      alert('Please enter valid order limits');
      return;
    }
    
    if (parseFloat(minOrderLimit) >= parseFloat(maxOrderLimit)) {
      alert('Maximum order limit must be greater than minimum');
      return;
    }
    
    navigate('/post-ad-step-3');
  };
  
  const handleBack = () => {
    navigate('/post-ad-step-1');
  };
  
  return (
    <div className="post-ad-container">
      <header className="post-ad-header">
        <div className="post-ad-header-container">
          <button className="post-ad-back-button" onClick={handleBack} aria-label="Go back">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="post-ad-title">Post an Ad</h1>
        </div>
        <div className="post-ad-progress">
          <div className="post-ad-progress-bar" style={{ width: '66.67%' }}></div>
        </div>
      </header>

      <div className="post-ad-content">
        <div className="post-ad-section">
          <h2 className="post-ad-section-title">Price and amount</h2>
          
          <label className="post-ad-label">Unit Price</label>
          <p className="post-ad-hint-description">Price of the cryptocurrency in fiat.</p>
          <p className="post-ad-market-rate">Current Market Rate: {marketRate.toFixed(4)} NGN/USDT</p>
          
          <div className="post-ad-input-with-currency">
            <input
              type="number"
              className="post-ad-price-input"
              placeholder="0"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
            />
            <span className="post-ad-currency-label">NGN</span>
          </div>
          
          <div className="post-ad-price-buttons">
            <button 
              className="post-ad-price-adjust-button"
              onClick={() => handlePriceAdjustment('minus')}
            >
              − 1%
            </button>
            <button 
              className="post-ad-price-adjust-button"
              onClick={() => handlePriceAdjustment('market')}
            >
              Market
            </button>
            <button 
              className="post-ad-price-adjust-button"
              onClick={() => handlePriceAdjustment('plus')}
            >
              + 1%
            </button>
          </div>
          
          <label className="post-ad-label">Total amount to buy</label>
          <p className="post-ad-hint-description-small">Total amount of crypto willing to buy.</p>
          
          <div className="post-ad-input-with-currency">
            <input
              type="number"
              className="post-ad-price-input"
              placeholder="0"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
            />
            <span className="post-ad-currency-label">USDT</span>
          </div>
          
          <label className="post-ad-label">Price Type</label>
          <p className="post-ad-hint-description">The type of pricing.</p>
          
          <div className="post-ad-radio-group">
            <button
              className={`post-ad-radio-option ${priceType === 'fixed' ? 'selected' : ''}`}
              onClick={() => setPriceType('fixed')}
            >
              <div className="post-ad-radio-left">
                <div className={`post-ad-radio-circle ${priceType === 'fixed' ? 'selected' : ''}`}>
                  {priceType === 'fixed' && <div className="post-ad-radio-dot"></div>}
                </div>
                <span className="post-ad-radio-label">Fixed</span>
              </div>
              {priceType === 'fixed' && (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16.25 6.25L7.5 15L3.75 11.25" stroke="#FF4907" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
            
            <button
              className={`post-ad-radio-option ${priceType === 'market' ? 'selected' : ''}`}
              onClick={() => setPriceType('market')}
            >
              <div className="post-ad-radio-left">
                <div className={`post-ad-radio-circle ${priceType === 'market' ? 'selected' : ''}`}>
                  {priceType === 'market' && <div className="post-ad-radio-dot"></div>}
                </div>
                <span className="post-ad-radio-label">Market</span>
              </div>
            </button>
            
            <button
              className={`post-ad-radio-option ${priceType === 'floating' ? 'selected' : ''}`}
              onClick={() => setPriceType('floating')}
            >
              <div className="post-ad-radio-left">
                <div className={`post-ad-radio-circle ${priceType === 'floating' ? 'selected' : ''}`}>
                  {priceType === 'floating' && <div className="post-ad-radio-dot"></div>}
                </div>
                <span className="post-ad-radio-label">Floating</span>
              </div>
            </button>
          </div>
          
          <label className="post-ad-label">Order Limit</label>
          <p className="post-ad-hint-description-large">Min to Max crypto amount willing to buy in a single trade.</p>
          
          <div className="post-ad-order-limit-container">
            <div className="post-ad-order-limit-input-group">
              <input
                type="number"
                className="post-ad-order-limit-input"
                placeholder="0"
                value={minOrderLimit}
                onChange={(e) => setMinOrderLimit(e.target.value)}
              />
              <span className="post-ad-order-limit-currency">NGN</span>
            </div>
            
            <svg className="post-ad-order-limit-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#71717A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            
            <div className="post-ad-order-limit-input-group">
              <input
                type="number"
                className="post-ad-order-limit-input"
                placeholder="0"
                value={maxOrderLimit}
                onChange={(e) => setMaxOrderLimit(e.target.value)}
              />
              <span className="post-ad-order-limit-currency">NGN</span>
            </div>
          </div>
          
          <button className="post-ad-next-button" onClick={handleNext}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostAdStep2;